import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers,
  Home,
  ArrowDown,
  Flame,
  Droplet,
  Wind,
  Gauge,
  Hammer,
  LucideIcon
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/utils/cn';

interface Recommendation {
  id: string;
  title: string;
  description: string;
  category: string;
  estimated_cost?: number;
  estimated_savings?: number;
  priority: 'CRITIQUE' | 'HAUTE' | 'MOYENNE' | 'BASSE' | 'INFO';
  difficulty?: string;
}

// Taux moyen France : 0,13€ par kWh d'énergie primaire économisée
// (électricité 0,25€/kWh final ÷ facteur 2,3 ≈ 0,11€/kWh EP ; gaz ≈ 0,12€/kWh EP)
const EURO_PER_KWH_EP = 0.13;

const estimateAnnualSavingsEuros = (savingsKwhPerM2: number, surfaceM2: number): number => {
  return Math.round(savingsKwhPerM2 * surfaceM2 * EURO_PER_KWH_EP);
};

interface RecommendedWorksProps {
  dpeId: string;
  immediate_actions?: Recommendation[];
  short_term_actions?: Recommendation[];
  isLegacyDPE?: boolean;
  surfaceHabitable?: number | null;
}

const getCategoryIcon = (category: string): LucideIcon => {
  const categoryLower = category.toLowerCase();

  if (categoryLower.includes('menuiserie') || categoryLower.includes('window')) {
    return Home;
  }
  if (categoryLower.includes('wall') || categoryLower.includes('mur')) {
    return Layers;
  }
  if (categoryLower.includes('roof') || categoryLower.includes('toit')) {
    return Home;
  }
  if (categoryLower.includes('floor') || categoryLower.includes('plancher')) {
    return ArrowDown;
  }
  if (categoryLower.includes('heating') || categoryLower.includes('chauffage')) {
    return Flame;
  }
  if (categoryLower.includes('hot_water') || categoryLower.includes('ecs') || categoryLower.includes('eau')) {
    return Droplet;
  }
  if (categoryLower.includes('ventilation')) {
    return Wind;
  }
  if (categoryLower.includes('regulation') || categoryLower.includes('control')) {
    return Gauge;
  }

  return Hammer;
};

const getCategoryColor = (category: string): string => {
  const categoryLower = category.toLowerCase();

  if (categoryLower.includes('menuiserie') || categoryLower.includes('window')) {
    return 'text-blue-600 bg-blue-50';
  }
  if (categoryLower.includes('wall') || categoryLower.includes('mur')) {
    return 'text-green-600 bg-green-50';
  }
  if (categoryLower.includes('roof') || categoryLower.includes('toit')) {
    return 'text-orange-600 bg-orange-50';
  }
  if (categoryLower.includes('floor') || categoryLower.includes('plancher')) {
    return 'text-cyan-600 bg-cyan-50';
  }
  if (categoryLower.includes('heating') || categoryLower.includes('chauffage')) {
    return 'text-red-600 bg-red-50';
  }
  if (categoryLower.includes('hot_water') || categoryLower.includes('ecs') || categoryLower.includes('eau')) {
    return 'text-sky-600 bg-sky-50';
  }
  if (categoryLower.includes('ventilation')) {
    return 'text-gray-600 bg-gray-50';
  }
  if (categoryLower.includes('regulation') || categoryLower.includes('control')) {
    return 'text-purple-600 bg-purple-50';
  }

  return 'text-gray-600 bg-gray-50';
};

const getPriorityBadge = (priority: string) => {
  switch (priority) {
    case 'CRITIQUE':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Critique
        </span>
      );
    case 'HAUTE':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          Haute
        </span>
      );
    case 'MOYENNE':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Moyenne
        </span>
      );
    default:
      return null;
  }
};

export const RecommendedWorks: React.FC<RecommendedWorksProps> = ({
  dpeId,
  immediate_actions = [],
  short_term_actions = [],
  isLegacyDPE = false,
  surfaceHabitable = null
}) => {
  const navigate = useNavigate();

  const filteredWorks = useMemo(() => {
    const excludedCategories = [
      'DPE_CORRECTION',
      'DPE_OPTIMIZATION',
      'SYSTEME_COLLECTIF',
      'REGLEMENTAIRE'
    ];

    const allActions = [...immediate_actions, ...short_term_actions];

    const filtered = allActions.filter(action => {
      const hasValidPriority = ['CRITIQUE', 'HAUTE', 'MOYENNE'].includes(action.priority);
      const isNotExcluded = !excludedCategories.includes(action.category);

      return hasValidPriority && isNotExcluded;
    });

    return filtered.sort((a, b) => {
      const priorityOrder = { CRITIQUE: 0, HAUTE: 1, MOYENNE: 2 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];

      if (priorityDiff !== 0) return priorityDiff;

      const savingsA = a.estimated_savings || 0;
      const savingsB = b.estimated_savings || 0;
      return savingsB - savingsA;
    });
  }, [immediate_actions, short_term_actions]);

  if (isLegacyDPE || filteredWorks.length === 0) {
    return null;
  }

  const handleNavigateToScenarios = () => {
    navigate(`/analysis/${dpeId}/scenarios`);

    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'click_recommended_works_cta', {
        event_category: 'engagement',
        event_label: 'recommended_works_section',
        dpe_id: dpeId
      });
    }
  };

  return (
    <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-blue-50">
      <CardHeader>
        <CardTitle className="text-2xl">Travaux conseillés pour votre logement</CardTitle>
        <CardDescription>
          Recommandations prioritaires basées sur votre DPE et vos réponses au questionnaire
        </CardDescription>
      </CardHeader>

      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {filteredWorks.map((work) => {
            const Icon = getCategoryIcon(work.category);
            const colorClass = getCategoryColor(work.category);

            return (
              <div
                key={work.id}
                className="bg-white rounded-lg p-4 border border-gray-200 hover:border-green-300 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start space-x-3 mb-3">
                  <div className={cn('p-2 rounded-lg', colorClass)}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900 text-sm leading-tight">
                        {work.title}
                      </h4>
                      {getPriorityBadge(work.priority)}
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                  {work.description}
                </p>

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  {work.estimated_savings && surfaceHabitable ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      ≈ {estimateAnnualSavingsEuros(work.estimated_savings, surfaceHabitable).toLocaleString('fr-FR')} €/an
                    </span>
                  ) : (
                    <span />
                  )}
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Recommandé
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-lg p-6 border-2 border-green-300 shadow-sm">
          <div className="text-center">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Combien ces travaux peuvent-ils vous faire économiser ?
              </h3>
              <p className="text-gray-600">
                Simulez leur impact sur votre classe DPE et estimez vos économies annuelles en euros
              </p>
            </div>

            <Button
              variant="primary"
              size="lg"
              onClick={handleNavigateToScenarios}
              icon={<Hammer className="w-5 h-5 mr-2" />}
              className="shadow-md hover:shadow-lg transition-shadow"
            >
              Simuler l'impact sur mon DPE
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
